(function () {
  const root = document.querySelector('[data-supra-cart]');
  if (!root) return;

  const body = root.querySelector('[data-supra-cart-body]');
  const footer = root.querySelector('[data-supra-cart-footer]');
  const countEl = root.querySelector('[data-supra-cart-count]');
  const note = root.querySelector('[data-supra-cart-shipping]');
  const errorEl = root.querySelector('[data-supra-cart-error]');
  const shippingText = root.querySelector('[data-supra-cart-shipping-text]');
  const shippingProgress = root.querySelector('[data-supra-cart-shipping-progress]');
  const totalEl = root.querySelector('[data-supra-cart-total]');
  const checkoutTotalEl = root.querySelector('[data-supra-cart-checkout-total]');
  const originalTotalEl = root.querySelector('[data-supra-cart-original-total]');
  const savingsEl = root.querySelector('[data-supra-cart-savings]');
  const savingsBanner = root.querySelector('[data-supra-cart-savings-banner]');
  const discountRow = root.querySelector('[data-supra-cart-discount-row]');
  const discountLabel = root.querySelector('[data-supra-cart-discount-label]');
  const discountCodesEl = root.querySelector('[data-supra-cart-discount-codes]');
  const initialJson = root.querySelector('[data-supra-cart-json]');
  const emptyUrl = root.dataset.emptyUrl || '/collections/all';
  let cart = parseInitialCart();
  let refreshPromise = null;
  let isMutating = false;
  let errorMessage = '';
  let cartUncertain = false;
  let updatingLine = null;
  let updatingPackId = null;
  let closeTimer = null;
  let returnFocus = null;
  const productCache = new Map();
  const products = new Map();
  const route = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;

  function parseInitialCart() {
    try {
      return JSON.parse(initialJson?.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  function currencyCode() {
    return cart.currency || window.Shopify?.currency?.active || document.documentElement.dataset.currency || 'EUR';
  }

  function formatMoney(cents) {
    const value = (Number(cents) || 0) / 100;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode(),
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  function formatCurrencyValue(value) {
    const amount = Number(value) || 0;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode(),
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function logoMarkup() {
    return `
      <img
        class="supra-cart__mark"
        src="https://cdn.shopify.com/s/files/1/0945/8955/0940/files/supra-logo-icon-black.svg?v=1772814273"
        alt=""
        loading="lazy"
      >
    `;
  }

  function itemOriginalLinePrice(item) {
    const compareAt = item.variant?.compare_at_price || item.compare_at_price || item._variant_compare_at_price;
    if (compareAt && compareAt > item.original_price) {
      return compareAt * item.quantity;
    }

    return item.original_line_price || item.original_price * item.quantity;
  }

  function itemFinalLinePrice(item) {
    return item.final_line_price ?? item.line_price ?? item.price * item.quantity;
  }

  function cartOriginalTotal() {
    return (cart.items || []).reduce((sum, item) => sum + itemOriginalLinePrice(item), 0);
  }

  function appliedCodeDiscounts(cartData) {
    const applications = [
      ...(cartData.cart_level_discount_applications || []),
      ...(cartData.items || []).flatMap((item) =>
        (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application).filter(Boolean)
      ),
    ];

    return applications
      .filter((application) => application.type === 'discount_code')
      .filter(
        (application, index, entries) =>
          entries.findIndex((entry) => (entry.title || entry.code) === (application.title || application.code)) === index
      );
  }

  function discountValueText(application) {
    if (application.value_type === 'percentage') return `${Number(application.value) || 0}% Off`;
    if (application.value_type === 'fixed_amount') return `${formatCurrencyValue(application.value)} Off`;
    return '';
  }

  function renderDiscountCodes() {
    if (!discountRow || !discountCodesEl || !discountLabel) return;

    const discounts = appliedCodeDiscounts(cart);
    const values = [...new Set(discounts.map(discountValueText).filter(Boolean))];
    discountRow.hidden = !discounts.length;
    discountLabel.textContent = values.length ? `Discount (${values.join(', ')})` : 'Discount';
    discountCodesEl.innerHTML = discounts
      .map(
        (discount) => `
          <span class="supra-cart__discount-pill">
            <span>${escapeHtml(discount.title || discount.code)}</span>
            <button
              type="button"
              aria-label="Remove discount code ${escapeHtml(discount.title || discount.code)}"
              data-supra-cart-remove-discount="${escapeHtml(discount.title || discount.code)}"
            >&times;</button>
          </span>
        `
      )
      .join('');
  }

  function variantText(item) {
    if (item.variant_title && item.variant_title !== 'Default Title') return item.variant_title;
    if (Array.isArray(item.options_with_values) && item.options_with_values.length) {
      return item.options_with_values.map((option) => option.value).join(', ');
    }
    return '';
  }

  function sellingPlanText(item) {
    return item.selling_plan_allocation?.selling_plan?.name || '';
  }

  function deliveryText(item) {
    return sellingPlanText(item) || 'Delivered once';
  }

  function updateShippingProgress(count) {
    const currency = currencyCode();
    const country = root.dataset.country;
    const threshold = country === 'IE' && currency === 'EUR' ? 6000 : country === 'GB' && currency === 'GBP' ? 5000 : 0;
    note.hidden = !count || !threshold;
    if (!threshold) return;
    const subtotal = Math.max(0, Math.round(Number(cart.total_price) || 0));
    const remaining = Math.max(0, threshold - subtotal);
    shippingText.textContent = remaining ? `Add ${formatMoney(remaining)} for free shipping.` : 'You have unlocked free shipping.';
    shippingProgress.style.width = `${Math.min(100, subtotal / threshold * 100)}%`;
    note.classList.toggle('is-unlocked', remaining === 0);
  }

  function productHandleForItem(item) {
    if (item.handle) return item.handle;
    const url = item.url || item.product_url || '';
    return url.split('/products/')[1]?.split(/[/?#]/)[0] || '';
  }

  async function productDataForHandle(handle) {
    if (!handle) return null;
    const key = `${route('')}:${currencyCode()}:${handle}`;
    if (productCache.has(key)) return productCache.get(key);

    const productPromise = fetch(route(`products/${encodeURIComponent(handle)}.js`), {
      credentials: 'same-origin',
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)
      .then((product) => {
        if (product) products.set(key, product);
        else productCache.delete(key);
        return product;
      });

    productCache.set(key, productPromise);
    return productPromise;
  }

  async function hydrateVariantCompareAtPrices(cartData) {
    const items = cartData.items || [];
    await Promise.all(
      items.map(async (item) => {
        const productData = await productDataForHandle(productHandleForItem(item));
        const variant = productData?.variants?.find((entry) => Number(entry.id) === Number(item.variant_id));
        if (variant?.compare_at_price) {
          item._variant_compare_at_price = Number(variant.compare_at_price);
        }
      })
    );
  }

  function packOptionsForItem(item) {
    if (!item || item.selling_plan_allocation || productHandleForItem(item) !== root.dataset.packProductHandle) return [];
    const product = products.get(`${route('')}:${currencyCode()}:${productHandleForItem(item)}`);
    if (!product || product.requires_selling_plan) return [];
    const current = product.variants.find((variant) => String(variant.id) === String(item.variant_id));
    if (!current) return [];
    const packSize = (value) => Number(String(value).match(/^(30|60|90)(?:\s*(?:sachets?|days?(?:\s+supply)?))?$/i)?.[1]) || 0;
    const packIndex = current.options.findIndex((value) => packSize(value));
    if (packIndex < 0) return [];
    // Keep other options (such as flavour) unchanged when switching pack size.
    const variants = product.variants.filter((variant) =>
      !variant.requires_selling_plan && packSize(variant.options[packIndex]) &&
      variant.options.every((value, index) => index === packIndex || value === current.options[index])
    );
    const base = variants.find((variant) => packSize(variant.options[packIndex]) === 30);
    return variants.map((variant) => {
      const size = packSize(variant.options[packIndex]);
      const reference = Number(variant.compare_at_price) > variant.price
        ? Number(variant.compare_at_price) : (base?.price || 0) * size / 30;
      const saving = reference > variant.price ? Math.round((reference - variant.price) / reference * 100) : 0;
      return { id: variant.id, size, available: variant.available, saving };
    }).sort((a, b) => a.size - b.size);
  }

  function packControlsMarkup(item, index) {
    const options = packOptionsForItem(item);
    if (!options.length) return '';
    const isUpdating = updatingLine === index + 1;
    return `
      <section class="supra-cart__packs" aria-labelledby="supra-cart-packs-${index}">
        <h3 class="supra-cart__packs-title" id="supra-cart-packs-${index}">Buy More &amp; Save</h3>
        <div class="supra-cart__plans" role="group" aria-labelledby="supra-cart-packs-${index}" aria-busy="${isUpdating}">
          ${options.map((option) => {
            const active = String(option.id) === String(item.variant_id);
            const loading = isUpdating && String(updatingPackId) === String(option.id);
            return `<button class="supra-cart__plan ${active ? 'is-active' : ''}" type="button"
              data-supra-cart-pack="${option.id}" aria-pressed="${active}"
              ${!option.available || isMutating ? 'disabled' : ''}>
              <span class="supra-cart__plan-radio" aria-hidden="true"></span>
              <span class="supra-cart__plan-copy"><span class="supra-cart__plan-title">${option.size} Sachets</span></span>
              ${loading ? '<span class="supra-cart__loading" aria-hidden="true"></span><span class="visually-hidden">Updating cart</span>'
                : `<strong>${!option.available ? 'Sold out' : option.saving && option.size > 30 ? `Save ${option.saving}%` : ''}</strong>`}
            </button>`;
          }).join('')}
        </div>
      </section>`;
  }

  function renderEmpty() {
    body.innerHTML = `
      <div class="supra-cart__empty">
        <div class="supra-cart__empty-inner">
          ${logoMarkup()}
          <p>Your cart is empty.</p>
          <a class="supra-cart__empty-button" href="${escapeHtml(emptyUrl)}">GET STARTED</a>
        </div>
      </div>
    `;
  }

  function renderItems() {
    body.innerHTML = (cart.items || [])
      .map((item, index) => {
        const original = itemOriginalLinePrice(item);
        const final = itemFinalLinePrice(item);
        const savings = Math.max(original - final, 0);
        const variant = variantText(item);
        const sellingPlan = deliveryText(item);
        const image = item.image
          ? `<img class="supra-cart__image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
          : logoMarkup();

        return `
          <article class="supra-cart__item" data-line="${index + 1}">
            <a class="supra-cart__image-link" href="${escapeHtml(item.url)}">${image}</a>
            <a class="supra-cart__item-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            <button class="supra-cart__remove" type="button" aria-label="Remove ${escapeHtml(item.product_title)}" data-supra-cart-remove>&times;</button>
            ${variant ? `<p class="supra-cart__meta supra-cart__variant">${escapeHtml(variant)}</p>` : ''}
            ${sellingPlan ? `<p class="supra-cart__meta supra-cart__selling-plan">${escapeHtml(sellingPlan)}</p>` : ''}
            <div class="supra-cart__quantity" aria-label="Quantity">
              <button type="button" data-supra-cart-quantity="-1" aria-label="Decrease quantity">-</button>
              <span>${item.quantity}</span>
              <button type="button" data-supra-cart-quantity="1" aria-label="Increase quantity">+</button>
            </div>
            <div class="supra-cart__line-price">
              ${savings > 0 ? `<s>${formatMoney(original)}</s> ` : ''}
              <span>${formatMoney(final)}</span>
              ${savings > 0 ? `<span class="supra-cart__line-saving">Save ${formatMoney(savings)}</span>` : ''}
            </div>
            ${packControlsMarkup(item, index)}
          </article>
        `;
      })
      .join('');
  }

  function syncHeaderCount() {
    document.querySelectorAll('.cart-bubble__text-count').forEach((el) => {
      el.textContent = cart.item_count ? String(cart.item_count) : '';
      el.classList.toggle('hidden', !cart.item_count);
    });

    document.querySelectorAll('.cart-bubble').forEach((el) => {
      el.classList.toggle('visually-hidden', !cart.item_count);
    });

    document.querySelectorAll('cart-icon').forEach((el) => {
      el.classList.toggle('header-actions__cart-icon--has-cart', Boolean(cart.item_count));
    });

    document.querySelectorAll('[data-instant-dynamic-content-source="CART_COUNT"]').forEach((el) => {
      el.textContent = String(cart.item_count || 0);
      el.hidden = !cart.item_count;
      el.classList.toggle('hidden', !cart.item_count);
      el.classList.toggle('is-hidden', !cart.item_count);
      el.setAttribute('aria-hidden', cart.item_count ? 'false' : 'true');
    });
  }

  function render() {
    const count = cart.item_count || 0;
    countEl.textContent = count ? String(count) : '';
    footer.hidden = !count;
    updateShippingProgress(count);
    renderDiscountCodes();

    if (!count) {
      renderEmpty();
    } else {
      renderItems();
      const originalTotal = cartOriginalTotal();
      const finalTotal = cart.total_price || 0;
      const savings = Math.max(originalTotal - finalTotal, 0);

      totalEl.textContent = formatMoney(finalTotal);
      if (checkoutTotalEl) checkoutTotalEl.textContent = formatMoney(finalTotal);
      originalTotalEl.hidden = !savings;
      originalTotalEl.textContent = savings ? formatMoney(originalTotal) : '';
      savingsBanner.hidden = !savings;
      savingsEl.textContent = savings ? `You're saving ${formatMoney(savings)}.` : '';
    }

    syncHeaderCount();
    errorEl.textContent = errorMessage;
    errorEl.hidden = !errorMessage;
    root.querySelectorAll('[data-supra-cart-quantity], [data-supra-cart-remove], [data-supra-cart-remove-discount]').forEach((button) => {
      button.disabled = isMutating;
    });
    root.querySelector('.supra-cart__checkout').setAttribute('aria-disabled', String(isMutating || cartUncertain));
    if (cartUncertain) {
      totalEl.textContent = '--';
      checkoutTotalEl.textContent = '--';
      originalTotalEl.hidden = true;
      savingsBanner.hidden = true;
      discountRow.hidden = true;
      note.hidden = true;
      root.querySelectorAll('.supra-cart__line-price').forEach((el) => { el.hidden = true; });
    }
  }

  function normalizeInstantCartLinks() {
    document.querySelectorAll('[data-instant-action-type="add-to-cart"][href]').forEach((link) => {
      const variantId = link.getAttribute('data-instant-action-variant-id');
      if (!variantId) return;

      const url = new URL(link.getAttribute('href'), window.location.origin);
      const currentQuantity = url.pathname.match(/\/cart\/\d+:(\d+)/)?.[1] || '1';
      const normalizedHref = `/cart/${variantId}:${currentQuantity}?storefront=true`;

      if (link.getAttribute('href') !== normalizedHref) {
        link.setAttribute('href', normalizedHref);
      }
    });
  }

  function instantFormForTrigger(trigger) {
    return trigger.closest('[data-instant-form-product-id], form[data-instant-type="root"], [data-instant-type="root"]');
  }

  function parseInstantQuantity(trigger) {
    try {
      const url = new URL(trigger.getAttribute('href') || '', window.location.origin);
      return Number(url.pathname.match(/\/cart\/\d+:(\d+)/)?.[1]) || 1;
    } catch (error) {
      return 1;
    }
  }

  function instantVariantIdForTrigger(trigger) {
    return (
      trigger.getAttribute('data-instant-action-variant-id') ||
      instantFormForTrigger(trigger)?.getAttribute('data-instant-form-variant-id') ||
      ''
    );
  }

  function normalizeSellingPlanId(value) {
    if (!value || value === 'one-time-purchase') return '';
    return value;
  }

  function instantSellingPlanIdForTrigger(trigger) {
    const directPlan = normalizeSellingPlanId(
      trigger.getAttribute('data-instant-action-selling-plan-id') ||
        trigger.getAttribute('data-selling-plan-id') ||
        trigger.getAttribute('selling-plan-id')
    );
    if (directPlan) return directPlan;

    const form = instantFormForTrigger(trigger);
    const activePlan = form?.querySelector(
      '[data-instant-action-type="select-selling-plan"][data-instant-state="active"], [data-instant-action-type="select-selling-plan"][aria-pressed="true"], [data-instant-action-type="select-selling-plan"].is-active'
    );
    const activePlanId = normalizeSellingPlanId(activePlan?.getAttribute('data-instant-selling-plan-id'));
    if (activePlanId || activePlan?.getAttribute('data-instant-selling-plan-id') === 'one-time-purchase') return activePlanId;

    return normalizeSellingPlanId(form?.getAttribute('data-instant-form-selling-plan-id'));
  }

  async function fetchLatestCart() {
    const response = await fetch(route('cart.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Unable to refresh your cart. Please try again.');
    return response.json();
  }

  async function refreshCart() {
    if (isMutating) return cart;
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      cart = await fetchLatestCart();
      cartUncertain = false;
      await hydrateVariantCompareAtPrices(cart);
      render();
      return cart;
    })().finally(() => { refreshPromise = null; });
    return refreshPromise;
  }

  async function cartRequest(endpoint, payload) {
    const response = await fetch(route(`cart/${endpoint}.js`), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Unable to update your cart. Please try again.');
    return result;
  }

  async function changeLine(line, quantity) {
    const item = typeof line === 'string' ? cart.items?.find((item) => item.key === line) : cart.items?.[line - 1];
    if (item) await cartRequest('change', { id: item.key, quantity });
  }

  async function removeDiscountCode(code) {
    const remainingCodes = appliedCodeDiscounts(cart)
      .map((discount) => discount.title || discount.code)
      .filter((entry) => entry !== code);
    await cartRequest('update', { discount: remainingCodes.join(',') });
  }

  async function addLine({ variantId, quantity, sellingPlanId }) {
    const item = {
      id: Number(variantId),
      quantity,
    };

    if (sellingPlanId) item.selling_plan = Number(sellingPlanId);

    // This path is only for product-page additions, including genuine subscriptions.
    await cartRequest('add', { items: [item] });
  }

  function propertiesKey(item) {
    return JSON.stringify(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  }

  function matchingOneTime(items, variantId, properties) {
    return items.filter((item) => !item.selling_plan_allocation && String(item.variant_id) === String(variantId) && propertiesKey(item) === properties);
  }

  async function replacePack(line, variantId) {
    const original = typeof line === 'string' ? cart.items?.find((item) => item.key === line) : cart.items?.[line - 1];
    const option = packOptionsForItem(original).find((entry) => String(entry.id) === String(variantId));
    if (!option?.available) throw new Error('That pack is currently unavailable. Please choose another size.');
    const properties = propertiesKey(original);
    const latest = await fetchLatestCart();
    const source = latest.items.find((item) => item.key === original.key);
    if (!source) throw new Error('Your cart changed. Please choose your pack again.');
    if (String(source.variant_id) === String(option.id)) {
      await cartRequest('change', { id: source.key, quantity: 1 });
      return;
    }
    let target = matchingOneTime(latest.items, option.id, properties)[0];
    let added = false;
    try {
      // Add first: a sold-out response must never remove the customer's existing pack.
      if (!target) {
        added = true;
        await cartRequest('add', { items: [{ id: option.id, quantity: 1, properties: original.properties || {} }] });
      }
      const afterAdd = await fetchLatestCart();
      const oldLine = matchingOneTime(afterAdd.items, original.variant_id, properties).find((item) => item.key === source.key)
        || matchingOneTime(afterAdd.items, original.variant_id, properties)[0];
      const targets = matchingOneTime(afterAdd.items, option.id, properties);
      target = targets[0];
      if (!oldLine || !target) throw new Error('Your cart changed. Please try again.');
      const updates = { [oldLine.key]: 0 };
      targets.forEach((item, index) => { updates[item.key] = index === 0 ? 1 : 0; });
      await cartRequest('update', { updates });
    } catch (error) {
      if (added) {
        // Reconcile an uncertain response before rollback; never recreate a removed source.
        const latestCart = await fetchLatestCart();
        const sourceStillPresent = matchingOneTime(latestCart.items, original.variant_id, properties).length;
        const addedTarget = matchingOneTime(latestCart.items, option.id, properties)[0];
        if (sourceStillPresent && addedTarget) await cartRequest('change', { id: addedTarget.key, quantity: 0 });
      }
      throw error;
    }
  }

  async function mutateCart(action, line = null, packId = null) {
    if (isMutating) return;
    isMutating = true;
    updatingLine = line;
    updatingPackId = packId;
    errorMessage = '';
    const active = document.activeElement;
    const restoreFocus = root.contains(active);
    render();
    if (restoreFocus && root.classList.contains('is-open')) root.querySelector('.supra-cart__drawer')?.focus();
    try {
      if (refreshPromise) await refreshPromise;
      await action();
    } catch (error) {
      errorMessage = error.message || 'Unable to update your cart. Please try again.';
    } finally {
      try {
        cart = await fetchLatestCart();
        cartUncertain = false;
        await hydrateVariantCompareAtPrices(cart);
      } catch (error) {
        cartUncertain = true;
        errorMessage = 'We could not confirm your cart. Close and reopen it to refresh before checkout.';
      }
      isMutating = false;
      updatingLine = null;
      updatingPackId = null;
      render();
      if (restoreFocus && root.classList.contains('is-open')) {
        const selected = packId && root.querySelector(`[data-supra-cart-pack="${packId}"][aria-pressed="true"]`);
        (selected || root.querySelector('.supra-cart__drawer'))?.focus();
      }
    }
  }

  function openCart() {
    if (!root.classList.contains('is-open')) returnFocus = document.activeElement;
    clearTimeout(closeTimer);
    root.classList.remove('is-closing');
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('supra-cart-open');
    root.querySelector('.supra-cart__drawer')?.focus();
  }

  function closeCart() {
    if (!root.classList.contains('is-open')) return;

    clearTimeout(closeTimer);
    root.classList.remove('is-open');
    root.classList.add('is-closing');
    root.setAttribute('aria-hidden', 'true');
    returnFocus?.focus();
    closeTimer = setTimeout(() => {
      root.classList.remove('is-closing');
      document.body.classList.remove('supra-cart-open');
    }, 240);
  }

  async function refreshAndOpen() {
    openCart();
    try {
      await refreshCart();
      errorMessage = '';
    } catch (error) {
      cartUncertain = true;
      errorMessage = 'Unable to refresh your cart. Please close and reopen it to try again.';
    }
    render();
  }

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-supra-cart-close]')) {
      closeCart();
      return;
    }
    if (isMutating || (cartUncertain && target.closest('.supra-cart__checkout'))) {
      event.preventDefault();
      return;
    }

    const removeDiscountButton = target.closest('[data-supra-cart-remove-discount]');
    if (removeDiscountButton) {
      mutateCart(() => removeDiscountCode(removeDiscountButton.dataset.supraCartRemoveDiscount));
      return;
    }

    const item = target.closest('[data-line]');
    if (!item) return;

    const line = Number(item.dataset.line);
    const key = cart.items?.[line - 1]?.key;
    const current = cart.items?.[line - 1]?.quantity || 0;

    if (target.closest('[data-supra-cart-remove]')) {
      mutateCart(() => changeLine(key, 0), line);
      return;
    }

    const packButton = target.closest('[data-supra-cart-pack]');
    if (packButton && !packButton.disabled) {
      const id = packButton.dataset.supraCartPack;
      mutateCart(() => replacePack(key, id), line, id);
      return;
    }

    const quantityButton = target.closest('[data-supra-cart-quantity]');
    if (quantityButton) {
      const delta = Number(quantityButton.dataset.supraCartQuantity);
      mutateCart(() => changeLine(key, Math.max(current + delta, 0)), line);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!root.classList.contains('is-open')) return;
    if (event.key === 'Escape') closeCart();
    if (event.key !== 'Tab') return;
    const focusable = [...root.querySelectorAll('button:not(:disabled), a[href], [tabindex="0"]')].filter((el) => el.getClientRects().length);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === root.querySelector('.supra-cart__drawer'))) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  });

  normalizeInstantCartLinks();

  let instantLinkObserverTimer;
  const scheduleInstantLinkNormalization = () => {
    clearTimeout(instantLinkObserverTimer);
    instantLinkObserverTimer = setTimeout(normalizeInstantCartLinks, 50);
  };

  const instantLinkObserver = new MutationObserver(scheduleInstantLinkNormalization);
  instantLinkObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener(
    'click',
    async (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest('[data-instant-action-type="add-to-cart"]');
      if (!trigger || root.contains(trigger)) return;

      const variantId = instantVariantIdForTrigger(trigger);
      if (!variantId) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const sellingPlanId = instantSellingPlanIdForTrigger(trigger);
      trigger.setAttribute('aria-busy', 'true');

      try {
        await addLine({
          variantId,
          quantity: parseInstantQuantity(trigger),
          sellingPlanId,
        });
        document.dispatchEvent(
          new CustomEvent('instant:add-to-cart', {
            detail: { variantId, sellingPlanId },
          })
        );
      } catch (error) {
        console.error(error);
      } finally {
        trigger.removeAttribute('aria-busy');
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest(
        'a[href="/cart"], a[href*="/cart"], button[data-testid="cart-drawer-trigger"], [data-instant-action-type*="cart"]'
      );

      if (!trigger || root.contains(trigger)) return;

      const href = trigger.getAttribute('href') || '';
      const actionType = trigger.getAttribute('data-instant-action-type') || '';
      if (actionType === 'add-to-cart') return;

      const isCartTrigger =
        actionType.includes('cart') ||
        href === '/cart' ||
        href.endsWith('/cart') ||
        href.includes('/cart?');

      if (!isCartTrigger) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      refreshAndOpen();
    },
    true
  );

  document.addEventListener('instant:add-to-cart', (event) => {
    event.stopPropagation();
    event.stopImmediatePropagation();
    setTimeout(refreshAndOpen, 250);
  }, true);

  ['cart:updated', 'cart:refresh', 'cart:change', 'ajaxProduct:added', 'ajaxProduct:updated'].forEach((eventName) => {
    document.addEventListener(eventName, () => refreshCart().catch(() => {
      cartUncertain = true;
      errorMessage = 'Unable to refresh your cart. Please close and reopen it to try again.';
      render();
    }));
  });

  window.openCartDrawer = refreshAndOpen;
  window.SUPRA_CART_DRAWER = {
    open: refreshAndOpen,
    close: closeCart,
    refresh: refreshCart,
  };

  render();
  hydrateVariantCompareAtPrices(cart).then(() => render());
})();
